import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import apiClient, { bindOrganizationIdProvider } from '@shared/lib/apiClient';
import { useAuth } from '@features/auth/AuthProvider';
const STORAGE_KEY = 'wallethub::activeOrganizationId';
const ActiveOrganizationContext = createContext(undefined);
export const ActiveOrganizationProvider = ({ children }) => {
    const { tokens, isBootstrapping, user } = useAuth();
    const isPlatformOperator = user?.role === 'super_admin';
    const [organizations, setOrganizations] = useState([]);
    const [activeOrganizationId, setActiveOrganizationIdState] = useState(null);
    const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
    useEffect(() => {
        let cancelled = false;
        if (!tokens || isBootstrapping) {
            setOrganizations([]);
            setActiveOrganizationIdState(null);
            bindOrganizationIdProvider(() => null);
            setIsLoadingOrganizations(false);
            return () => {
                cancelled = true;
            };
        }
        setIsLoadingOrganizations(true);
        void (async () => {
            try {
                const { data } = await apiClient.get('/organizations');
                if (cancelled)
                    return;
                setOrganizations(data);
                const stored = localStorage.getItem(STORAGE_KEY);
                const fallback = data[0]?.id ?? null;
                const next = stored && data.some((o) => o.id === stored) ? stored : fallback;
                setActiveOrganizationIdState(next);
                if (next) {
                    localStorage.setItem(STORAGE_KEY, next);
                }
                else {
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
            catch {
                if (!cancelled) {
                    setOrganizations([]);
                    setActiveOrganizationIdState(null);
                }
            }
            finally {
                if (!cancelled) {
                    setIsLoadingOrganizations(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tokens, isBootstrapping]);
    useEffect(() => {
        bindOrganizationIdProvider(() => activeOrganizationId);
    }, [activeOrganizationId]);
    const setActiveOrganizationId = useCallback((id) => {
        setActiveOrganizationIdState(id);
        localStorage.setItem(STORAGE_KEY, id);
    }, []);
    const clearActiveOrganization = useCallback(() => {
        setActiveOrganizationIdState(null);
        localStorage.removeItem(STORAGE_KEY);
    }, []);
    const refreshOrganizations = useCallback(async () => {
        if (!tokens || isBootstrapping)
            return;
        const { data } = await apiClient.get('/organizations');
        setOrganizations(data);
        setActiveOrganizationIdState((prev) => {
            if (prev && data.some((o) => o.id === prev)) {
                return prev;
            }
            const stored = localStorage.getItem(STORAGE_KEY);
            const fromStorage = stored && data.some((o) => o.id === stored) ? stored : null;
            const next = fromStorage ?? data[0]?.id ?? null;
            if (next) {
                localStorage.setItem(STORAGE_KEY, next);
            }
            else {
                localStorage.removeItem(STORAGE_KEY);
            }
            return next;
        });
    }, [tokens, isBootstrapping]);
    const value = useMemo(() => ({
        organizations,
        activeOrganizationId,
        activeOrganization: activeOrganizationId != null
            ? organizations.find((organization) => organization.id === activeOrganizationId) ?? null
            : null,
        setActiveOrganizationId,
        clearActiveOrganization,
        isLoadingOrganizations,
        refreshOrganizations,
        isPlatformOperator,
        isAuditMode: isPlatformOperator && activeOrganizationId != null,
    }), [
        organizations,
        activeOrganizationId,
        setActiveOrganizationId,
        clearActiveOrganization,
        isLoadingOrganizations,
        refreshOrganizations,
        isPlatformOperator,
    ]);
    return (_jsx(ActiveOrganizationContext.Provider, { value: value, children: children }));
};
export const useActiveOrganization = () => {
    const ctx = useContext(ActiveOrganizationContext);
    if (!ctx) {
        throw new Error('useActiveOrganization must be used within ActiveOrganizationProvider');
    }
    return ctx;
};
